import { BULB_COLORS, PALETTE_PRESETS } from './palettes'
import { PRODUCTS } from './products'
import type { ProductId } from '../types/document'

/**
 * WHAT ACTUALLY GOES ON THE HOUSE.
 *
 * CREW-02. A quote says what it costs and an approval says what was agreed;
 * neither of them says what to put in the van or where the wire goes. That is
 * this, and every line of it comes off the APPROVED copy rather than the live
 * design, which is the whole reason the copy exists. A crew picking against
 * live geometry is cutting wire for a roof somebody edited this morning.
 *
 * THE NUMBERS ARE FOR TALKING. Run four is short is a sentence somebody says
 * on a phone from a ladder. They are positions in the approved list; the
 * section id underneath is what anything is keyed on, and never the other way
 * round, because a number is a label and a label carries no identity. Same
 * rule lib/approval.ts already states for the pick list.
 *
 * THE PACKET CAN BE OUT OF DATE, AND SAYS SO. If the live sections have moved
 * since the scope was frozen, the sheet in somebody's hand is describing a
 * different house from the one on the screen. The spec asks for the exact
 * approved packet to be downloaded before departure; there is nothing to
 * download here, because jobs and photographs are already on the device, so
 * the honest version of that safeguard is a banner saying the design has been
 * edited since this was agreed.
 */

/** A color said the way somebody says it out loud, not as a hex value. */
export function colorName(hex: string): string {
  const h = hex.trim().toUpperCase()
  for (const [key, value] of Object.entries(BULB_COLORS)) {
    if (value.toUpperCase() === h) {
      // warmWhite becomes Warm white: the words a person uses on a ladder.
      const spaced = key.replace(/([A-Z])/g, ' $1').toLowerCase().trim()
      return spaced.charAt(0).toUpperCase() + spaced.slice(1)
    }
  }
  return hex
}

/**
 * A WHOLE PALETTE, NAMED WHERE IT HAS A NAME.
 *
 * The presets already carry the words a shop uses for the common ones, so Red
 * and green comes out as Red and green rather than as two hex codes stapled
 * together. Anything hand mixed falls back to naming each color.
 */
export function paletteName(palette: string[]): string {
  if (palette.length === 0) return 'No color set'
  const mine = palette.map((c) => c.trim().toUpperCase()).join(',')
  for (const p of PALETTE_PRESETS) {
    if (p.colors.map((c) => c.trim().toUpperCase()).join(',') === mine) return p.label
  }
  const names = palette.map(colorName)
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** One line of the pattern, when there is a pattern rather than one color. */
export function patternWording(palette: string[], repeat: number): string {
  if (palette.length <= 1) return ''
  const n = Math.max(1, Math.round(repeat))
  return n === 1
    ? 'One of each, repeating'
    : `${n} of each, repeating`
}

/** What a run is, in the words a crew uses. */
export interface PacketRun {
  id: string
  name: string
  /** C9, C7, mini, permanent. */
  product: ProductId
  productLabel: string
  spacingInches: number
  palette: string[]
  paletteLabel: string
  pattern: string
}

/**
 * One numbered stop on the house: the section, its cut length, what it takes,
 * and the runs that actually sit on it.
 */
export interface PacketSection {
  n: number
  id: string
  label: string
  kind: string
  feet: number
  bulbs: number
  clips: number
  /** True when this length was walked at the house rather than estimated. */
  verified: boolean
  runs: PacketRun[]
}

export interface PacketTotals {
  feet: number
  bulbs: number
  clips: number
  /** Feet of wire including the allowance, which is why it is not feet. */
  wire: number
}

export interface Packet {
  approvalId: string
  approvedAt: string
  approvedBy: string
  packageName: string
  sections: PacketSection[]
  totals: PacketTotals
  /** Non empty when the live design has moved since the scope was frozen. */
  staleBecause: string[]
}

/** The strand fields this file needs, so it never imports the document type. */
export interface RunSource {
  id: string
  name: string
  product: ProductId
  palette: string[]
  patternRepeat: number
}

/**
 * THE LOAD LIST IS A SUM OF THE ROWS, NOT A SECOND CALCULATION.
 *
 * Two places arriving at a bulb count independently is two places that can
 * disagree, and the one a crew reads is the one that matters. The rows come
 * from lib/approval.ts pickList; this adds them up and nothing more.
 */
export function totalsOf(sections: PacketSection[], wireAllowance: number): PacketTotals {
  let feet = 0
  let bulbs = 0
  let clips = 0
  for (const s of sections) {
    feet += s.feet
    bulbs += s.bulbs
    clips += s.clips
  }
  return {
    feet: Math.round(feet),
    bulbs,
    clips,
    // Rounded up: nobody has ever wanted to be four feet short of the corner.
    wire: Math.ceil(feet * wireAllowance),
  }
}

/** Turns a run into the three or four things somebody reads off a ladder. */
export function describeRun(r: RunSource): PacketRun {
  const spec = PRODUCTS[r.product] ?? PRODUCTS.C9
  return {
    id: r.id,
    name: r.name.trim() || 'Unnamed run',
    product: r.product,
    productLabel: spec.label,
    spacingInches: spec.spacingInches,
    palette: r.palette,
    paletteLabel: paletteName(r.palette),
    pattern: patternWording(r.palette, r.patternRepeat),
  }
}

/**
 * The parts of CREW-02 that have nothing behind them, named on the screen
 * rather than left as gaps a crew discovers on a roof.
 */
export const NOT_IN_THE_PACKET: string[] = [
  'A numbered diagram on the photograph. These numbers are positions in the approved list.',
  'Which clip, and which end the cord runs to. Nothing records that.',
  'A download step. Jobs and photographs are already on this device.',
  'Reporting a problem, or asking for extra work. Neither can travel back to the office yet, so anything extra is a phone call rather than a silent change to an approved scope.',
]
